import { TextField } from 'components/form'
import { Button } from 'components/button'
import { Flex } from 'components/elements/Flex'
import { DownArrowIcon, UpArrowIcon } from 'components/icons'
import { useGetPolicies } from '../api/getPolicies'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useGetFeatures } from '../api/getFeatures'
import { useEditPolicies } from '../api/cancelPolicies'
import { AxiosError } from 'axios'
import { convertDocUrlsToBase64, downloadPdfDocument, errorMessage } from 'utils/function'
import { ErrorBox, ModalForm } from 'features/profile/components/EditProfileForm'
import { useNavigate } from 'react-router-dom'
import { BREACHRX_CIRM_COMPANY_ID, routes } from 'configs/constants'
import { CoveragePeriod, NotificationMessages, PolicyStatus, TERMINOLOGY } from 'utils/enum'
import PreviousRecord from './PreviousRecord'
import { PDF } from 'components/pdf/PDF'
import { Modal } from 'components/modal'
import ImageDocBox from './ImageDocBox'
import PageNotFound from 'components/notFound/PageNotFound'
import { CircularProgress, LinearProgress, Tooltip } from '@mui/material'
import { InsurancePolicyEnum, ModalEnum } from 'features/incidents/components/enums'
import {
  EmptyRecord,
  InsurancePolicyCss,
  MainCard,
  OverflowScroll,
  ShowLessTextCss,
  StyledRow,
  StyledStatusBox,
  StyledTable,
  ToolTipcss,
} from 'features/incidents/components/style'
import { Box } from 'components/elements'
import { Circle, StatusBar } from '../pages/Insurance'
import AddService from './AddService'
import ChangePolicy from './ChangePolicy'
import ManualPayment from './ManualPayment'
import SnackBar from 'components/SnackBar'
import { wrapStyle } from 'views/Support/SupportForm'
import { useMediaQuery } from 'react-responsive'
import { StyledCell } from 'features/incidents/components/SentinelOne/IncidentsTable'
import { useSecureScoreControlStatus } from 'features/incidents/api/securescore'

type InsuranceProps = {
  openModal: { label: string; isOpen: boolean }
  setOpenModal: (value: { label: string; isOpen: boolean }) => void
  setHasALLServices: (value: boolean) => void
  setNotification: (value: string) => void
}

const InsurancePolicy = (props: InsuranceProps) => {
  const { openModal, setOpenModal, setHasALLServices, setNotification } = props

  const navigate = useNavigate()
  const isDesktopOrLaptop = useMediaQuery({ minWidth: 991 })
  const imageRefs: React.MutableRefObject<(HTMLImageElement | null)[]> = useRef([])
  const [Modalpopup, setModal] = useState(false)
  const [withdraw, setWithdraw] = useState('')
  const [submitError, setSubmitError] = useState('')
  const [downloadFiles, setDownloadFiles] = useState<string[]>([])
  const [manualPaymentModal, setOpenManualPaymentModal] = useState(false)
  const [manualPaymentNotification, setManualPaymentNotification] = useState(false)
  const [cancelPopup, setCancelPopup] = useState(false)
  const [showCaseHistory, setShowCaseHistory] = useState<boolean>(false)
  const [downloadStatus, setDownloadStatus] = useState<boolean>(true)
  const [status, setStatus] = useState('')
  const { data: currentPolicy, isLoading: PolicyLoading } = useGetPolicies()

  const doc: Document = document
  const body = doc.querySelector('body')
  const companyName = currentPolicy?.data?.companyName
  const planId: number = currentPolicy?.data.planId
  const insurancePolicies = currentPolicy?.data
  //current services
  const currentServices = insurancePolicies?.services?.map((item: { serviceId: number }) => item.serviceId) || []
  // company services
  const companyServices = insurancePolicies?.planServices
  const planPrimaryService = insurancePolicies?.primaryServiceId
  const companyServiesCount = companyServices?.length
  const basePremium = insurancePolicies?.paymentPeriod === CoveragePeriod.MONTHLY ? insurancePolicies?.monthlyPremium : insurancePolicies?.annualPremium
  const discountAmount = insurancePolicies?.discountedAmount
  const discountedAmount = insurancePolicies?.discountedPremium
  const primaryServiceId = insurancePolicies?.primaryServiceId
  const isClaimEnabled = insurancePolicies?.isClaimEnabled
  // The controls table is a Microsoft Secure Score measure. BreachRx CIRM runs on
  // that integration but is sold as its own service, so their holders have no
  // tenant of their own behind these controls and should not be shown them.
  const isBreachRxCirmPlan = insurancePolicies?.companyId === BREACHRX_CIRM_COMPANY_ID
  const isControlStatusEnabled = primaryServiceId === 6 && isClaimEnabled && !isBreachRxCirmPlan
  const { data: controlStatus, isLoading: isControlStatusLoading } = useSecureScoreControlStatus(isControlStatusEnabled)
  const controlRows = useMemo(() => controlStatus ?? [], [controlStatus])

  const downloadFile = insurancePolicies?.documentUrl?.urls
  const fileNames = insurancePolicies?.documentUrl?.docNames
  const { data: features } = useGetFeatures({
    insurancePlanId: planId,
  })
  const { mutate: cancelPolicy, isLoading } = useEditPolicies()
  const controlsStatusSectionId = 'controls-status'

  useEffect(() => {
    manualPaymentModal ? body?.classList.add('overflow--hidden--modal') : body?.classList.remove('overflow--hidden--modal')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualPaymentModal])
  useEffect(() => {
    setStatus(currentPolicy?.data?.planStatus)
    setHasALLServices(insurancePolicies?.services?.length === companyServiesCount)
  }, [currentPolicy, insurancePolicies, companyServiesCount, setHasALLServices])

  useEffect(() => {
    imageRefs.current = imageRefs.current.slice(0, downloadFile?.length)
  }, [downloadFile])

  useEffect(() => {
    const pdfFile = async () => {
      const insuraceUrl = await convertDocUrlsToBase64(downloadFile)
      setDownloadFiles(insuraceUrl)
    }
    pdfFile()
  }, [downloadFile])

  const onSubmit = () => {
    const editPolicy = {
      confirmationText: withdraw,
    }
    setSubmitError('')
    cancelPolicy(editPolicy, {
      onSuccess: () => {
        setCancelPopup(true)
        setModal(false)
      },
      onError: error => {
        if (error instanceof AxiosError) {
          setSubmitError(errorMessage(error))
        }
      },
    })
  }

  const handleDownloadPdfDocument = () => {
    setDownloadStatus(false)

    const pdfElement = [document.getElementById('divToDownload')].filter((element): element is HTMLElement => element !== null)
    downloadPdfDocument(pdfElement, undefined, setDownloadStatus, 'Report')
  }

  const goToControlsStatusSection = () => {
    const el = document.getElementById(controlsStatusSectionId)
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <>
      {PolicyLoading ? (
        <CircularProgress />
      ) : currentPolicy?.message == InsurancePolicyEnum.MESSAGE ? (
        <PageNotFound />
      ) : (
        <>
          <MainCard css={InsurancePolicyCss}>
            <Flex justify="between" direction="column">
              <Box as="div" css={download} id="divToDownload">
                <Flex css={paymentWrap} justify={'between'}>
                  <Box css={FlexBoxCSS}>
                    <Box as="p" css={{ color: '$gray4' }}>
                      {TERMINOLOGY.partner.singular} Name
                    </Box>
                    <Box as="h3" css={{ marginTop: '$4' }}>
                      {companyName}
                    </Box>
                  </Box>
                  <Box>
                    <Flex wrap={'wrap'}>
                      <Box>
                        {status !== undefined && (
                          <>
                            {status === InsurancePolicyEnum.PENDING ? (
                              <Tooltip
                                PopperProps={{ sx: ToolTipcss }}
                                title={
                                  <Box css={{ ...wrapStyle, textAlign: 'left', padding: 0 }} className="fullWidth">
                                    <Box as="p" css={{ color: '$black' }}>
                                      To activate policy, please connect primary service attached in monitoring services,&nbsp;
                                      <Box as="span" css={{ color: '$primary', cursor: 'pointer' }} onClick={() => navigate(routes.assessments)}>
                                        Click here
                                      </Box>
                                    </Box>
                                  </Box>
                                }
                                placement="bottom"
                                enterTouchDelay={0}
                              >
                                <StatusBar>
                                  <Circle className={'cancelled'} />
                                  <Box as="p" css={{ marginBottom: 0, fontWeight: '$bold' }}>
                                    {status}
                                  </Box>
                                </StatusBar>
                              </Tooltip>
                            ) : (
                              <StatusBar
                                role={status === InsurancePolicyEnum.SERVICES_MISCONFIGURED ? 'button' : undefined}
                                tabIndex={status === InsurancePolicyEnum.SERVICES_MISCONFIGURED ? 0 : undefined}
                                onClick={status === InsurancePolicyEnum.SERVICES_MISCONFIGURED ? goToControlsStatusSection : undefined}
                                onKeyDown={
                                  status === InsurancePolicyEnum.SERVICES_MISCONFIGURED
                                    ? e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault()
                                          goToControlsStatusSection()
                                        }
                                      }
                                    : undefined
                                }
                                css={{ cursor: status === InsurancePolicyEnum.SERVICES_MISCONFIGURED ? 'pointer' : 'default' }}
                              >
                                <Circle
                                  className={`${
                                    status === InsurancePolicyEnum.ACTIVED ? 'active' : status === InsurancePolicyEnum.SERVICES_MISCONFIGURED ? 'misconfigured' : 'cancelled'
                                  }`}
                                />
                                <Box as="p" css={{ marginBottom: 0, fontWeight: '$bold' }}>
                                  {status}
                                </Box>
                              </StatusBar>
                            )}
                          </>
                        )}
                      </Box>
                      <Box>
                        {insurancePolicies?.isRenewEnable && (
                          <Box css={{ padding: '0 1.5rem' }}>
                            <Button onClick={() => setOpenManualPaymentModal(true)} size="sm">
                              Payment
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Flex>
                  </Box>
                </Flex>
                <Flex direction={'row'} css={{ ...FlexBoxCSS, ...HeadingBorderCss }}>
                  <Box as="p" css={{ flexBasis: '30%', ...headingCSS }}>
                    {TERMINOLOGY.warranty.singular} Name
                  </Box>
                  <Box as="p" css={{ flex: 1, ...textCSS }}>
                    {insurancePolicies?.planName}
                  </Box>
                </Flex>
                <Flex direction={'row'} css={{ ...FlexBoxCSS, ...HeadingBorderCss }}>
                  <Box as="p" css={{ flexBasis: '30%', ...headingCSS }}>
                    {TERMINOLOGY.warranty.singular} Description
                  </Box>
                  <Box as="p" css={{ flex: 1, ...textCSS }}>
                    {insurancePolicies?.description}
                  </Box>
                </Flex>
                {features?.data.insurancePolicies.length !== 0 && (
                  <>
                    <Flex direction={'row'} css={{ ...FlexBoxCSS, ...HeadingBorderCss }}>
                      <Box as="p" css={{ ...headingCSS }}>
                        {TERMINOLOGY.warranty.singular} Features
                      </Box>
                    </Flex>
                    <Box>
                      {features?.data.insurancePolicies.map((item: { title: string; description: string }, index: number) => (
                        <Flex direction={'row'} css={{ ...FlexBoxCSS, ...HeadingBorderCss }} key={index}>
                          <Box as="p" css={{ flexBasis: '30%', ...headingCSS }}>
                            {item.title ? item.title : 'No title added yet'}
                          </Box>
                          <Box as="p" css={{ flex: 1, ...textCSS }}>
                            {item.description ? item.description : 'No description added against this feature'}
                          </Box>
                        </Flex>
                      ))}
                    </Box>
                  </>
                )}
                <Flex direction={'row'} css={{ ...FlexBoxCSS, ...HeadingBorderCss }}>
                  <Box as="p" css={{ flexBasis: '30%', ...headingCSS }}>
                    {TERMINOLOGY.benefit.singular} Amount
                  </Box>
                  <Box as="p" css={{ flex: 1, margin: 0 }}>
                    {`$${insurancePolicies?.maxCoverage ?? 0}`}
                  </Box>
                </Flex>

                {isControlStatusEnabled && (
                  <Box id={controlsStatusSectionId} css={{ ...HeadingBorderCss, margin: '0 1.5rem 1.5rem', borderRadius: 8 }}>
                    <Box as="button" type="button" role="tab" aria-selected={true} css={getTabButtonCss(true)}>
                      Controls Status
                    </Box>

                    <Box as="p" css={{ margin: '0.75rem 0 0', fontSize: '$base', fontWeight: '$medium' }}>
                      The following Controls within Microsoft Secure Score MUST be enabled and configured to 100% to order for a M365 policy to be considered active and generate
                      applicable claims
                    </Box>

                    <Box css={{ ...OverflowScroll, marginTop: '1rem' }}>
                      <StyledTable css={{ marginBottom: 0 }} className={`${!isDesktopOrLaptop && controlRows.length ? 'table--mobile' : 'responsive-mobile'} `}>
                        {isDesktopOrLaptop && (
                          <thead>
                            <StyledRow>
                              <StyledCell as="th" className="minWidth-175">
                                Control
                              </StyledCell>
                              <StyledCell as="th" className="minWidth-175">
                                Status
                              </StyledCell>
                              <StyledCell as="th" className="minWidth-175">
                                Score
                              </StyledCell>
                            </StyledRow>
                          </thead>
                        )}
                        <tbody>
                          {isControlStatusLoading ? (
                            <th colSpan={3}>
                              <LinearProgress sx={{ height: '6px' }} />
                            </th>
                          ) : controlRows.length ? (
                            controlRows.map((control: any, idx: number) => {
                              const name = control?.controlName ?? '-'
                              const status = control?.status ?? '-'
                              const score = typeof control?.scoreInPercentage === 'number' ? `${control.scoreInPercentage}%` : '-'
                              const statusCss = getStatusPillCss(status)

                              return (
                                <StyledRow key={`${name}-${idx}`}>
                                  <StyledCell>
                                    {!isDesktopOrLaptop && (
                                      <Box as="h4" css={{ mb: 0 }}>
                                        Control
                                      </Box>
                                    )}
                                    {name}
                                  </StyledCell>
                                  <StyledCell>
                                    {!isDesktopOrLaptop && (
                                      <Box as="h4" css={{ mb: 0 }}>
                                        Status
                                      </Box>
                                    )}
                                    <StyledStatusBox as="span" css={{ margin: 0, ...statusCss }}>
                                      {status}
                                    </StyledStatusBox>
                                  </StyledCell>
                                  <StyledCell>
                                    {!isDesktopOrLaptop && (
                                      <Box as="h4" css={{ mb: 0 }}>
                                        Score
                                      </Box>
                                    )}
                                    {score}
                                  </StyledCell>
                                </StyledRow>
                              )
                            })
                          ) : (
                            <EmptyRecord colSpan={3}>No Data</EmptyRecord>
                          )}
                        </tbody>
                      </StyledTable>
                    </Box>
                    <Box as="p" css={{ margin: '0.75rem 0 0', color: '$gray600', fontSize: '0.95rem' }}>
                      For any issues connecting services refer to the video on the Monitoring Services page, but if further assistance is needed reach out to us using the Support
                      button at the bottom left.
                    </Box>
                  </Box>
                )}

                <Flex direction={'row'} css={{ ...FlexBoxCSS, ...HeadingBorderCss }}>
                  <Box as="p" css={{ flexBasis: '30%', ...headingCSS }}>
                    {TERMINOLOGY.warranty.singular} Documents
                  </Box>
                </Flex>
                {downloadFiles.length !== 0 ? (
                  <>
                    <Box css={FlexBoxCSS}>
                      <Box as="p" css={{ marginBottom: '1.25rem' }}></Box>
                      <ImageDocBox scourceData={downloadFiles} documentName={fileNames} hideLabel={true} />
                    </Box>
                  </>
                ) : (
                  <Box as="p" css={{ p: '1rem 1.5rem' }}>
                    No document is attached to this plan.
                  </Box>
                )}
              </Box>

              <Flex css={{ ...ButtonWrapCss, padding: '2rem 1.5rem' }}>
                <Button bordered={true} onClick={() => setModal(true)} disabled={!insurancePolicies || insurancePolicies.planStatus === PolicyStatus.Cancelled ? true : false}>
                  Cancel {TERMINOLOGY.warranty.singular}
                </Button>
                <Button onClick={handleDownloadPdfDocument} disabled={!downloadStatus}>
                  {downloadStatus === false ? 'Downloading...' : ' Download All Documents'}
                </Button>
                {!!insurancePolicies?.planStatus && <PDF />}
              </Flex>
            </Flex>
          </MainCard>
          <Modal
            active={Modalpopup}
            hideModal={() => setModal(false)}
            onAccept={onSubmit}
            title={`Cancel your ${TERMINOLOGY.warranty.singular.toLowerCase()}`}
            description={`Are you sure you want to cancel ${insurancePolicies?.planName}`}
            children={
              <ModalForm onSubmit={onSubmit}>
                {!!submitError && <ErrorBox>{submitError}</ErrorBox>}
                <Box as="p">
                  Note: By clicking on “Cancel {TERMINOLOGY.warranty.singular.toLowerCase()}”, you will not be able to initiate a claim on this{' '}
                  {TERMINOLOGY.warranty.singular.toLowerCase()} again.
                </Box>
                <TextField
                  fullWidth
                  label={`Write Cancel to cancel ${TERMINOLOGY.warranty.singular.toLowerCase()}`}
                  type="text"
                  value={withdraw}
                  onChange={event => setWithdraw(event.target.value)}
                />
              </ModalForm>
            }
            isForm={true}
            acceptText={isLoading ? 'Cancelling...' : `Cancel ${TERMINOLOGY.warranty.singular}`}
          />
          <Modal
            active={cancelPopup}
            onAccept={() => {
              setCancelPopup(false)
              setModal(false)
            }}
            title="Success"
            description={`You have successfully Cancelled   ${insurancePolicies?.planName}`}
            acceptText={'Ok'}
            isHidden={true}
          />
          {/* Previous record list  */}

          <Box css={detailButtonCss} style={{ marginTop: '2rem' }} onClick={() => setShowCaseHistory(!showCaseHistory)}>
            <Box as="h4" css={ShowLessTextCss}>
              {showCaseHistory ? `Hide ${TERMINOLOGY.warranty.plural}' History` : `Show ${TERMINOLOGY.warranty.plural}' History`}
            </Box>
            {showCaseHistory ? <UpArrowIcon fontSize={22} /> : <DownArrowIcon fontSize={22} />}
          </Box>
          {showCaseHistory && <PreviousRecord />}
        </>
      )}
      {openModal.label === ModalEnum.ADD_SERVICES && (
        <AddService
          planPrimaryService={planPrimaryService}
          openModal={openModal}
          setNotification={setNotification}
          setOpenModal={setOpenModal}
          currentServices={currentServices}
          companyServices={companyServices}
        />
      )}
      {openModal.label === ModalEnum.CHANGE_POLICY && <ChangePolicy planId={planId} openModal={openModal} setNotification={setNotification} setOpenModal={setOpenModal} />}
      {/* Manual Payment */}
      {manualPaymentModal && (
        <ManualPayment
          setOpenManualPaymentModal={setOpenManualPaymentModal}
          manualPaymentModal={manualPaymentModal}
          basePremium={basePremium}
          discountAmount={discountAmount}
          discountedAmount={discountedAmount}
          setManualPaymentNotification={setManualPaymentNotification}
        />
      )}
      <SnackBar
        open={manualPaymentNotification}
        onClose={() => {
          setManualPaymentNotification(false)
        }}
        text={NotificationMessages.Manual_Payment}
        severity={'success'}
      />
    </>
  )
}
export const headingCSS = {
  margin: '0',
  // color: '$gray4',
  fontSize: '1rem',
  fontWeight: 700,
}
export const textCSS = {
  margin: '0',
  wordBreak: 'break-word',
}
export const download = {
  backgroundImage: ' linear-gradient( 90.18deg, $background 0%, $background 15%)',
  paddingTop: '20px',
}
export const ButtonWrapCss = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  width: '100%',
  gap: '20px',
}

export const paymentWrap = {
  'gap': '1.25rem',
  'flexWrap': 'wrap-reverse',

  '@sm': {
    flexWrap: 'nowrap',
  },
}

export const FlexBoxCSS = {
  'marginBottom': '1.5rem',
  'padding': '0 1.5rem',
  'flexDirection': 'column',
  'rowGap': '$3',

  '@sm': {
    flexDirection: 'row',
  },
}

export const ModalbuttonCss = {
  justifyContent: 'center',
  alignItems: 'center',
  flexWrap: 'wrap',
  width: '100%',
  margin: '2.5rem auto 0',
  gap: '20px',
}

export const detailButtonCss = {
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
  cursor: 'pointer',
  width: '100%',
  background: '$primary10',
  padding: '.5rem 1.5rem',
  borderRadius: 4,
}

export const HeadingBorderCss = {
  border: '2px solid $gray8',
  background: '$blue2',
  padding: '1rem 1.5rem',
}

const TabButtonBaseCss = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'default',
  fontSize: '1.125rem',
  fontWeight: '$bold',
  paddingBottom: '0.5rem',
  borderBottom: '2px solid $primary',
} as const

const getTabButtonCss = (isActive: boolean) => ({
  ...TabButtonBaseCss,
  color: isActive ? '$text' : '$gray600',
  borderBottom: isActive ? '2px solid $primary' : '2px solid transparent',
})

const StatusPillBaseCss = {
  padding: '4px 10px',
  borderRadius: '999px',
  display: 'inline-block',
} as const

const StatusPillEnabledCss = {
  ...StatusPillBaseCss,
  backgroundColor: '$successLight',
  color: '$success',
} as const

const StatusPillDisabledCss = {
  ...StatusPillBaseCss,
  backgroundColor: '$errorLight',
  color: '$error',
} as const

const StatusPillUnknownCss = {
  ...StatusPillBaseCss,
  backgroundColor: '$gray200',
  color: '$gray700',
} as const

const getStatusPillCss = (status: string) => {
  if (status === 'Enabled') return StatusPillEnabledCss
  if (status === 'Disabled') return StatusPillDisabledCss
  return StatusPillUnknownCss
}
export default InsurancePolicy
